'use client';

import Button from '@carvana/ds-button';
import { useContext } from 'react';
import PrimaryCtaButton from '@/vdp/CtaButton/PrimaryCtaButton';
import SecondaryCtaButton from '@/vdp/CtaButton/SecondaryCtaButton';
import TextCtaButton from '@/vdp/CtaButton/TextCtaButton';
import getOutOfAreaVariant from '@/vdp/VehicleInfoCard/OutOfAreaErrorContent/getOutOfAreaVariant';
import { SectionName } from '~/constants/vdp/section';
import ModalContext from '~/context/vdp/ModalContext';
import {
  SectionAnalyticsProvider,
  useSectionAnalytics
} from '~/context/vdp/SectionAnalyticsContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';

function VdpComposerCta() {
  const { analyticsId, analyticsAttributes } = useSectionAnalytics();
  const { setGeolocationModalOpen } = useContext(ModalContext);
  const { notAvailableInCA, outOfMarket, outOfMarketNewCar } = useAlertStatus();
  const outOfAreaVariant = getOutOfAreaVariant({
    notAvailableInCA,
    outOfMarket,
    outOfMarketNewCar
  });

  if (outOfAreaVariant) {
    return (
      <Button
        className="w-full !h-[36px]"
        data-adobe-id={null}
        data-analytics-id={outOfAreaVariant.analyticsId}
        data-testid="composer-change-location"
        onClick={() => setGeolocationModalOpen(true)}
        size="xs"
        variant="secondary"
      >
        Change Location
      </Button>
    );
  }

  return (
    // !h-[36px] on both buttons below: the Figma spec calls for a 36px-tall button, but
    // ds-button has no size token between xs (32px) and sm (40px) — xs's own padding/radius
    // otherwise match the spec exactly, so this overrides just the height rather than
    // accepting the 4px drift from xs's native size.
    <div className="flex w-full gap-md">
      {/*
        variant is pinned to "secondary" because SecondaryCtaButton otherwise switches to
        "primaryInverted" whenever showCtaFooter is true (e.g. purchase-in-progress), a style
        meant to sit on CtaWrapper's dark footer banner. The Composer never renders that banner,
        so letting the auto-switch through renders as an unstyled text link on this white card.
      */}
      <SecondaryCtaButton
        className="flex-1 items-center justify-center !h-[36px] !bg-background-primary !text-text-inverse !shadow-none hover:!bg-background-primary-hover"
        data-analytics-id={analyticsId}
        data-analytics-attributes={analyticsAttributes}
        data-testid="composer-secondary-cta"
        size="xs"
        variant="secondary"
      />
      <TextCtaButton
        className="flex flex-1 items-center justify-start"
        data-analytics-id={analyticsId}
        data-analytics-attributes={analyticsAttributes}
        data-testid="composer-text-cta"
        size="sm"
      />
      <PrimaryCtaButton
        className="flex-1 items-center justify-center !h-[36px]"
        data-analytics-id={analyticsId}
        data-analytics-attributes={analyticsAttributes}
        data-testid="composer-primary-cta"
        size="xs"
      />
    </div>
  );
}

export default function VdpComposerSlot() {
  return (
    <SectionAnalyticsProvider
      analyticsId={SectionName.VehicleInfo}
      analyticsAttributes={{ inComposer: true, isSticky: true }}
    >
      <VdpComposerCta />
    </SectionAnalyticsProvider>
  );
}
